<template>
	<Skeleton :paragraph="{ rows: 7 }" :title="false" :loading="loading">
		<Form :model="formState" :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false">
			<FormItem :label="$t('settings.vrsSettingsForm.expirationDaysLogs')"
				v-bind="validateInfos.expirationDaysLogs">
				<InputDecimal v-model="formState.expirationDaysLogs" :allowPoint="false" :minValue="1" />
			</FormItem>
			<FormItem v-bind="validateInfos.expirationDaysRinexFiles">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.expirationDaysFtpFilesHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.vrsSettingsForm.expirationDaysFtpFiles') }}
					</Tooltip>
				</template>
				<InputDecimal v-model="formState.expirationDaysRinexFiles" :allowPoint="false" range="non-negative" />
			</FormItem>
			<FormItem>
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.expirationDaysRinexFilesHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.vrsSettingsForm.expirationDaysRinexFiles') }}
					</Tooltip>
				</template>
				<InputDecimal :modelValue="expirationDaysRinexFilesDisplay" :allowPoint="false" :minValue="2" disabled />
			</FormItem>
			<FormItem v-if="licenseStore.allowBaselines" v-bind="validateInfos.expirationDaysNmeaFiles">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.expirationDaysNmeaFilesHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.vrsSettingsForm.expirationDaysNmeaFiles') }}
					</Tooltip>
				</template>
				<InputDecimal v-model="formState.expirationDaysNmeaFiles" :allowPoint="false" :minValue="2" />
			</FormItem>
			<FormItem v-bind="validateInfos.expirationDaysRinexStats">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.expirationDaysRinexStatsHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.vrsSettingsForm.expirationDaysRinexStats') }}
					</Tooltip>
				</template>
				<InputDecimal v-model="formState.expirationDaysRinexStats" :allowPoint="false" :minValue="2" />
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.expirationDaysProducts')"
				v-bind="validateInfos.expirationDaysProducts">
				<InputDecimal v-model="formState.expirationDaysProducts" :allowPoint="false" :minValue="1" />
			</FormItem>
			<FormButtonsItem>
				<Button type="primary" @click.prevent="onSubmit" :loading="isSubmitting">
					{{ $t('settings.vrsSettingsForm.buttonLabel') }}
				</Button>
				<TaskRunButton taskLabel="run_script_cleaner" />
			</FormButtonsItem>
		</Form>
	</Skeleton>
</template>

<script>
import { reactive, computed, nextTick, toRaw, watch } from 'vue';
import { Button, Skeleton, Form, FormItem, Tooltip } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Validators from '@/api/Validators';
import Utils from '@/api/Utils';

import { useVRSSettingsPost } from './useVRSSettingsPost.js'

import InputDecimal from '@/components/common/InputDecimal';
import TaskRunButton from '@/components/common/TaskRunButton';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';
import { useLicenseStore } from '@/stores/licenseStore';

export default {
	components: {
		Button, Skeleton, Form, FormItem,
		InputDecimal, TaskRunButton, Tooltip, QuestionCircleOutlined, FormButtonsItem,
	},
	props: ['loading', 'settings'],
	setup(props) {
		const licenseStore = useLicenseStore();
		const formState = reactive({
			expirationDaysLogs: 0,
			expirationDaysRinexFiles: 0,
			expirationDaysRinexStats: 0,
			expirationDaysNmeaFiles: 0,
			expirationDaysProducts: 0,
		});
		const { validate, validateInfos } = Validators.useVRSSettingsCleanerFormRules(formState);
		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'CleanerSettingsForm');
		const expirationDaysRinexFilesDisplay = computed(() => Math.max(Number(formState.expirationDaysRinexFiles) || 0, 2));
		const { isSubmitting, onSubmit } = useVRSSettingsPost(Api.vrsSettingsPost, formState, validate, toRaw, resetChangedState);
		watch(
			() => props.settings,
			value => {
				pauseWatch();
				Utils.updateObject(formState, value);
				nextTick(enableWatch);
			},
		);
		return {
			isSubmitting,
			formState,
			expirationDaysRinexFilesDisplay,
			validateInfos,
			onSubmit,
			licenseStore,
		};
	},
}
</script>
